/**
 * Model & runtime — the Model configuration this agent RESOLVES to when the
 * service runs (task 796).
 *
 * This panel is the only surface in the app that shows a flow's node-level
 * `llmConfigId` override, so the resolution it displays has to be the engine's,
 * not an approximation of it. `resolveLlmConfig`
 * (`innovate-service/src/lib/llm/resolver.ts`) is a lowest-to-highest cascade
 * called once per agent node from `collaborationNodeExecutors.ts`, and it emits
 * the same three source names this panel renders:
 *
 *   1. **Node override** — the agent node in this flow carries its own
 *      `llmConfigId`, and that config resolves.
 *   2. **Agent default** — the agent's own `modelConfigId`.
 *   3. **Environment** — neither is set, so the service falls back to its
 *      process-level `DEFAULT_MODEL_PROVIDER` / `DEFAULT_MODEL` /
 *      `DEFAULT_MODEL_MAX_TOKENS` / `DEFAULT_MODEL_TEMPERATURE` defaults (renamed
 *      from `LLM_*` by task 1073). These are the *service's* runtime defaults,
 *      not one of the deployment Environments on the Config tab — a name
 *      collision the copy below is careful to defuse.
 *
 * Two engine behaviours this deliberately mirrors rather than tidies away:
 *
 *   - **A node override that does not resolve falls through silently.** If the
 *     node names a config that is missing or soft-deleted, the engine logs a
 *     warning and runs on the agent default; the run does not fail. So does this
 *     panel, and it says so out loud, because a User looking at a stale override
 *     otherwise has no way to learn it is inert.
 *   - **Agent defaults are not frozen by a published version.** Under a pinned
 *     endpoint version the agent document comes from the pin set, but the engine
 *     re-reads the live agent row for its `modelConfigId`. "Agent default" here
 *     therefore describes the current config, which is exactly what will run.
 */
import { Link } from 'react-router-dom';
import { PlusIcon } from '@heroicons/react/24/outline';
import { useModelConfig, useModelConfigs } from '@/hooks/useModelConfigs';
import { useAccountModelPicks } from '@/hooks/useAccountModels';
import { useFlag } from '@/hooks/useFeatureFlags';
import {
  ModelConfigPickerOptions,
  ModelLifecycleNote,
  ModelPolicyNote,
} from '@/components/modelconfigs/ModelConfigPickerOptions';
import { FailoverModelConfigPicker } from '@/components/modelconfigs/FailoverModelConfigPicker';
import type { Agent, ModelConfig } from '@/types';
import { promptCachingLabel } from '@/utils/formatUtils';

/** The engine's own source names (`LlmConfigSource` in the resolver). */
export type LlmConfigSource = 'node-override' | 'agent-default' | 'environment';

export interface ResolvedLlmConfig {
  source: LlmConfigSource;
  /** The resolved config, or null in the `environment` case (no record exists). */
  config: ModelConfig | null;
  /**
   * A node-level `llmConfigId` that could not be read back. The engine warns and
   * falls through to the tier below; so does this, and the panel surfaces it.
   */
  unresolvedNodeConfigId?: string;
  isLoading: boolean;
}

/**
 * Resolve the config for one agent at one node, mirroring the engine's cascade.
 *
 * Exported because the rail cards show the same resolved model short name; there
 * must be exactly one implementation of the precedence in the frontend.
 */
export function useResolvedLlmConfig(
  agent: Agent | undefined,
  nodeLlmConfigId: string | undefined
): ResolvedLlmConfig {
  const nodeConfig = useModelConfig(nodeLlmConfigId);
  const agentConfig = agent?.modelConfig ?? null;

  if (nodeLlmConfigId) {
    if (nodeConfig.isLoading) {
      return { source: 'node-override', config: null, isLoading: true };
    }
    if (nodeConfig.data) {
      return { source: 'node-override', config: nodeConfig.data, isLoading: false };
    }
    // Unreadable override — the engine falls through, and reports which id it
    // could not honour.
    return {
      source: agentConfig ? 'agent-default' : 'environment',
      config: agentConfig,
      unresolvedNodeConfigId: nodeLlmConfigId,
      isLoading: false,
    };
  }

  if (agentConfig) {
    return { source: 'agent-default', config: agentConfig, isLoading: false };
  }

  return { source: 'environment', config: null, isLoading: false };
}

const SOURCE_CHIP: Record<LlmConfigSource, { label: string; classes: string }> = {
  'node-override': {
    label: 'Node override',
    classes:
      'border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-600 dark:bg-amber-900/30 dark:text-amber-200',
  },
  'agent-default': {
    label: 'Agent default',
    classes:
      'border-indigo-300 bg-indigo-50 text-indigo-700 dark:border-indigo-500 dark:bg-indigo-900/30 dark:text-indigo-200',
  },
  environment: {
    label: 'Environment',
    classes:
      'border-gray-300 bg-gray-50 text-gray-600 dark:border-gray-600 dark:bg-gray-700/50 dark:text-gray-300',
  },
};

const SOURCE_NOTE: Record<LlmConfigSource, string> = {
  'node-override':
    'This flow’s node overrides the agent’s own Model config — changing the agent default will not change what runs here.',
  'agent-default':
    'No node-level override in this flow — changes to the agent’s default config apply here.',
  environment:
    'Neither the agent nor this flow’s node names a Model config, so the service runs on its own configured provider defaults. That is the innovate-service runtime configuration, not one of this account’s deployment environments.',
};

/**
 * Coerce a numeric field that may not arrive as a JS number.
 *
 * The per-MTok prices live on the config's Model (task 1074); the service stores them
 * as Prisma decimals and serialises them as STRINGS (`"3"`, `"15"`) — a
 * `typeof value === 'number'` guard silently renders every real price as an
 * em-dash while the numeric test fixtures pass. Coercing here keeps the fix in
 * the one panel that reads these fields rather than restating the platform's
 * response shape.
 */
const toNumber = (value: unknown): number | null => {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

const formatNumber = (value: unknown): string => {
  const parsed = toNumber(value);
  return parsed === null ? '—' : parsed.toLocaleString();
};

const formatPrice = (value: unknown): string => {
  const parsed = toNumber(value);
  return parsed === null ? '—' : `$${parsed.toFixed(2)}`;
};

/**
 * The rows derived from a Model config: read-only in both modes, and in editing
 * mode they follow the selected config live (task 1310).
 */
function DerivedConfigRows({ config }: { config: ModelConfig }) {
  return (
    <>
      <dt className="text-gray-500 dark:text-gray-400">Temperature</dt>
      <dd className="text-gray-900 dark:text-gray-100">{config.temperature}</dd>

      <dt className="text-gray-500 dark:text-gray-400">Max output</dt>
      <dd className="text-gray-900 dark:text-gray-100">
        {formatNumber(config.maxOutputTokens)} tokens
      </dd>

      {/* The window the engine summarizes against: the config's
          budget when set, else the Model's whole window (task 1073). */}
      <dt className="text-gray-500 dark:text-gray-400">Context window</dt>
      <dd className="text-gray-900 dark:text-gray-100">
        {formatNumber(config.contextBudgetTokens ?? config.model.contextWindow)} tokens
      </dd>

      {/* Omitted entirely rather than printed as a row of dashes when
          the config carries no pricing. */}
      {(toNumber(config.model.inputPriceMTok) !== null ||
        toNumber(config.model.outputPriceMTok) !== null) && (
        <>
          <dt className="text-gray-500 dark:text-gray-400">Pricing</dt>
          <dd className="text-gray-900 dark:text-gray-100">
            {formatPrice(config.model.inputPriceMTok)} in / {formatPrice(config.model.outputPriceMTok)}{' '}
            out
            <span className="text-gray-500 dark:text-gray-400"> per MTok</span>
          </dd>
        </>
      )}

      {/* Same states as the Model Config detail card (task 1126);
          omitted when the provider has no prompt caching. */}
      {promptCachingLabel(config) && (
        <>
          <dt className="text-gray-500 dark:text-gray-400">Prompt caching</dt>
          <dd data-testid="agent-prompt-caching" className="text-gray-900 dark:text-gray-100">
            {promptCachingLabel(config)}
          </dd>
        </>
      )}
    </>
  );
}

interface AgentModelPanelProps {
  agent: Agent | undefined;
  /** The `llmConfigId` this flow's node declares, if any. */
  nodeLlmConfigId: string | undefined;
  /**
   * Render the Model config and failover pickers (task 1310). The page passes
   * the edited agent as `agent`, so the pickers show the draft.
   */
  editing?: boolean;
  /** Called on each picker change; the page accumulates these into its changed fields. */
  onFieldChange?: (field: string, value: unknown) => void;
}

const selectClasses =
  'block w-full rounded-md border-gray-300 bg-white text-sm text-gray-900 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100';

const configLabel = (config: ModelConfig) =>
  `${config.name || config.model.modelId} (${config.model.provider?.key ?? 'unknown'})`;

/**
 * Editing mode (task 1310): the pickers moved here from `EditableCompactConfig`.
 * A separate component so the catalog and policy reads happen only while
 * editing, never on the service tab's read-only view.
 */
function AgentModelPanelEditor({
  agent,
  onFieldChange,
}: {
  agent: Agent;
  onFieldChange?: (field: string, value: unknown) => void;
}) {
  const { data: modelConfigsData, isLoading } = useModelConfigs(1, 100);
  // The account's model policy (task 1147): disallowed configs grey out, the default is marked.
  const modelPolicy = useAccountModelPicks();
  const modelConfigs = modelConfigsData?.data ?? [];
  // Picking a catalog model is open to everyone; adding one to the shared
  // catalog is Monarch staff work (task 569).
  const canManageCatalog = useFlag('innovate.fe.model-configs.create');
  // The derived rows follow the selection against the loaded list, not the
  // agent's stale `modelConfig` relation.
  const selected = modelConfigs.find((config) => config.id === agent.modelConfigId) ?? null;

  return (
    <section
      data-testid="agent-model-panel"
      data-editing="true"
      className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-800"
    >
      <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Model &amp; runtime</h3>
      <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
        The Model configuration this agent runs on by default
      </p>

      <div className="mt-4 space-y-3 text-sm">
        <div>
          <label htmlFor="agent-edit-model-config" className="block text-xs font-medium text-gray-500 dark:text-gray-400">
            Model config
          </label>
          <div className="mt-1 flex items-center gap-2">
            <select
              id="agent-edit-model-config"
              data-testid="agent-edit-model-config"
              value={agent.modelConfigId || ''}
              onChange={(event) => {
                const id = event.target.value || null;
                onFieldChange?.('modelConfigId', id);
                // The service refuses the same config as primary and failover.
                if (id && id === agent.failoverModelConfigId) {
                  onFieldChange?.('failoverModelConfigId', null);
                }
              }}
              disabled={isLoading}
              className={`flex-1 ${selectClasses}`}
            >
              <option value="">No default config</option>
              <ModelConfigPickerOptions
                configs={modelConfigs}
                selectedId={agent.modelConfigId}
                policy={modelPolicy}
                label={configLabel}
              />
            </select>
            {canManageCatalog && (
              <Link
                to="/model-configs/new"
                data-testid="agent-edit-new-model-config"
                className="inline-flex items-center rounded-md border border-gray-300 bg-white px-2 py-1.5 text-sm text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
                title="Create new Model config"
              >
                <PlusIcon className="h-4 w-4" />
              </Link>
            )}
          </div>
          <ModelLifecycleNote configs={modelConfigs} selectedId={agent.modelConfigId} />
          <ModelPolicyNote configs={modelConfigs} selectedId={agent.modelConfigId} policy={modelPolicy} />
        </div>

        {/* Failover Model Config (task 1301) */}
        <FailoverModelConfigPicker
          configs={modelConfigs}
          policy={modelPolicy}
          primaryId={agent.modelConfigId}
          value={agent.failoverModelConfigId}
          onChange={(id) => onFieldChange?.('failoverModelConfigId', id)}
          disabled={isLoading}
        />
      </div>

      <dl
        data-testid="agent-model-derived-rows"
        className="mt-4 grid grid-cols-[minmax(0,9rem)_1fr] gap-x-4 gap-y-2 text-sm"
      >
        <dt className="text-gray-500 dark:text-gray-400">Model</dt>
        <dd className="font-mono text-xs text-gray-900 dark:text-gray-100">
          {selected ? (
            <>
              {selected.model.modelId}
              <span className="ml-2 font-sans text-gray-500 dark:text-gray-400">
                · {selected.model.provider?.key ?? 'unknown'}
              </span>
            </>
          ) : (
            <span className="font-sans text-gray-500 dark:text-gray-400">Service defaults</span>
          )}
        </dd>
        {selected && <DerivedConfigRows config={selected} />}
      </dl>

      <p className="mt-3 border-t border-gray-100 pt-3 text-xs text-gray-500 dark:border-gray-700 dark:text-gray-400">
        {selected
          ? 'The rows above follow the selected Model config. Temperature and output limits are edited on the config itself.'
          : 'Leave Model config empty and the agent runs on the service\u2019s own defaults.'}
      </p>
    </section>
  );
}

export function AgentModelPanel({
  agent,
  nodeLlmConfigId,
  editing = false,
  onFieldChange,
}: AgentModelPanelProps) {
  if (editing && agent) {
    return <AgentModelPanelEditor agent={agent} onFieldChange={onFieldChange} />;
  }
  return <AgentModelPanelView agent={agent} nodeLlmConfigId={nodeLlmConfigId} />;
}

function AgentModelPanelView({
  agent,
  nodeLlmConfigId,
}: Pick<AgentModelPanelProps, 'agent' | 'nodeLlmConfigId'>) {
  const resolved = useResolvedLlmConfig(agent, nodeLlmConfigId);
  const chip = SOURCE_CHIP[resolved.source];
  const config = resolved.config;

  return (
    <section
      data-testid="agent-model-panel"
      data-llm-source={resolved.source}
      className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-800"
    >
      <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Model &amp; runtime</h3>
      <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
        Configuration this agent resolves at execution time
      </p>

      {resolved.isLoading ? (
        <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">Resolving configuration…</p>
      ) : (
        <>
          <dl className="mt-4 grid grid-cols-[minmax(0,9rem)_1fr] gap-x-4 gap-y-2 text-sm">
            <dt className="text-gray-500 dark:text-gray-400">Model config</dt>
            <dd className="flex flex-wrap items-center gap-2 text-gray-900 dark:text-gray-100">
              {config ? (
                <Link
                  to={`/model-configs/${config.id}`}
                  className="text-indigo-600 hover:text-indigo-800 dark:text-indigo-400 dark:hover:text-indigo-300"
                >
                  {config.name ?? config.model.modelId}
                </Link>
              ) : (
                <span className="text-gray-500 dark:text-gray-400">Service defaults</span>
              )}
              <span
                data-testid="llm-source-chip"
                className={`inline-flex items-center whitespace-nowrap rounded-full border px-2 py-0.5 text-[11px] font-medium ${chip.classes}`}
              >
                {chip.label}
              </span>
            </dd>

            <dt className="text-gray-500 dark:text-gray-400">Model</dt>
            <dd className="font-mono text-xs text-gray-900 dark:text-gray-100">
              {config ? (
                <>
                  {config.model.modelId}
                  <span className="ml-2 font-sans text-gray-500 dark:text-gray-400">
                    · {config.model.provider?.key ?? 'unknown'}
                  </span>
                </>
              ) : (
                <span className="font-sans text-gray-500 dark:text-gray-400">
                  Set by the service, not recorded here
                </span>
              )}
            </dd>

            {/* The agent's failover (task 1301): used only when the primary's
                attempts are exhausted by transport errors or invalid replies.
                A node override replaces the primary, never the failover. */}
            <dt className="text-gray-500 dark:text-gray-400">Failover</dt>
            <dd data-testid="agent-failover-row" className="text-gray-900 dark:text-gray-100">
              {agent?.failoverModelConfig ? (
                <>
                  <Link
                    to={`/model-configs/${agent.failoverModelConfig.id}`}
                    className="text-indigo-600 hover:text-indigo-800 dark:text-indigo-400 dark:hover:text-indigo-300"
                  >
                    {agent.failoverModelConfig.name ?? agent.failoverModelConfig.model.modelId}
                  </Link>
                  <span className="ml-2 text-gray-500 dark:text-gray-400">
                    · {agent.failoverModelConfig.model.provider?.key ?? 'unknown'}
                  </span>
                </>
              ) : (
                <span className="text-gray-500 dark:text-gray-400">None</span>
              )}
              {resolved.source === 'node-override' && (
                <p data-testid="agent-failover-override-note" className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                  Node overrides do not change the failover.
                </p>
              )}
            </dd>

            {config && <DerivedConfigRows config={config} />}
          </dl>

          {resolved.unresolvedNodeConfigId && (
            <p
              data-testid="llm-unresolved-override"
              className="mt-3 rounded border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-700 dark:bg-amber-900/25 dark:text-amber-200"
            >
              This flow’s node names Model config{' '}
              <span className="font-mono">{resolved.unresolvedNodeConfigId}</span>, which cannot be
              read. The service logs a warning and runs on the configuration above instead.
            </p>
          )}

          <p className="mt-3 border-t border-gray-100 pt-3 text-xs text-gray-500 dark:border-gray-700 dark:text-gray-400">
            {SOURCE_NOTE[resolved.source]}
          </p>
        </>
      )}
    </section>
  );
}

export default AgentModelPanel;
